import { mergePreferences, type ResolvedPreferences, type UserPreferences } from '../types'
import {
  clearCachedPreferences as defaultClearCache,
  readCachedPreferences as defaultReadCache,
  writeCachedPreferences as defaultWriteCache,
} from './preferencesCache'
import {
  getPreferences as defaultApiGet,
  updatePreferences as defaultApiUpdate,
} from '../api/preferencesApi'

/**
 * Preferences in-memory store + orchestrator (Track D)。
 *
 * - module-level state + subscribe で React 外からも更新を流せる
 * - `loadInitial()`: app 起動 / login 完了時に呼ぶ。cache → API の順で fill
 * - `setPreference()`: optimistic update + API + cache。失敗時は revert
 *
 * deps DI で spec から network / IndexedDB を差し替え可能。
 */

export interface PreferencesStoreDeps {
  apiGet: typeof defaultApiGet
  apiUpdate: typeof defaultApiUpdate
  readCache: typeof defaultReadCache
  writeCache: typeof defaultWriteCache
  clearCache: typeof defaultClearCache
}

const defaultDeps: PreferencesStoreDeps = {
  apiGet: defaultApiGet,
  apiUpdate: defaultApiUpdate,
  readCache: defaultReadCache,
  writeCache: defaultWriteCache,
  clearCache: defaultClearCache,
}

export type PreferencesListener = (prefs: ResolvedPreferences) => void

class PreferencesStore {
  private partial: UserPreferences = {}
  private listeners = new Set<PreferencesListener>()
  private deps: PreferencesStoreDeps = defaultDeps

  /** spec で deps を差し替える */
  public configure(deps: Partial<PreferencesStoreDeps>): void {
    this.deps = { ...defaultDeps, ...deps }
  }

  public getResolved(): ResolvedPreferences {
    return mergePreferences(this.partial)
  }

  public getPartial(): UserPreferences {
    return { ...this.partial }
  }

  public subscribe(listener: PreferencesListener): () => void {
    this.listeners.add(listener)

    return () => {
      this.listeners.delete(listener)
    }
  }

  private notify(): void {
    const snapshot = this.getResolved()
    for (const l of this.listeners) {
      try {
        l(snapshot)
      } catch {
        // listener throw は握りつぶし (sessionStore と同じポリシー)
      }
    }
  }

  /** ログアウト等で完全リセット (cache も削除) */
  public async clear(): Promise<void> {
    this.partial = {}
    this.notify()
    try {
      await Promise.resolve(this.deps.clearCache())
    } catch {
      // cache 削除失敗は無視
    }
  }

  /**
   * 初期ロード: cache → API の順で fill。
   * - cache hit: 即座に store に反映 (UI が早く正しい値で render)
   * - その後 API で refresh して cache + store を更新
   */
  public async loadInitial(): Promise<void> {
    try {
      const cached = await this.deps.readCache()
      if (cached !== null) {
        this.partial = cached
        this.notify()
      }
    } catch {
      // cache 読み込み失敗は無視
    }

    try {
      const res = await this.deps.apiGet()

      this.partial = res.preferences
      this.notify()
      try {
        await Promise.resolve(this.deps.writeCache(this.partial))
      } catch {
        // cache write 失敗は無視
      }
    } catch {
      // network 失敗時は cache または defaults で動作継続
    }
  }

  /**
   * 単一フィールド更新。optimistic に local store + cache を先に書き、API を投げる。
   * 失敗時は revert + throw する (caller が Toast を出す責務)。
   */
  public async setPreference<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ): Promise<void> {
    const prevPartial = { ...this.partial }
    const nextPartial: UserPreferences = { ...this.partial, [key]: value }

    // optimistic local 更新
    this.partial = nextPartial
    this.notify()

    try {
      const res = await this.deps.apiUpdate({ [key]: value } as UserPreferences)

      this.partial = res.preferences
      this.notify()
      try {
        await Promise.resolve(this.deps.writeCache(this.partial))
      } catch {
        // cache write 失敗は無視
      }
    } catch (err) {
      // revert
      this.partial = prevPartial
      this.notify()
      throw err
    }
  }
}

export const preferencesStore = new PreferencesStore()
